// ============================================================================
// notificationActions.ts — действия карточки уведомления (клиент, SSoT).
//
// Используются существующие API (сервер/proto НЕ менялись):
//   • «Ответить» — переход в чат (lib/chatNavigation) + запрос фокуса поля ввода
//     (ChatThread снимает запрос при монтировании/смене диалога или по событию,
//     если чат уже открыт);
//   • «Прочитать» — чат: messenger.markConversationRead (через chatBridge);
//     уведомление платформы: MetadataService.MarkNotificationsRead.
//
// ОГРАНИЧЕНИЕ: у платформенных уведомлений нет серверного метода «отметить ОДНО
// уведомление» — существующий MarkNotificationsRead отмечает все непрочитанные.
// Отдельная отметка потребует изменения proto/сервера (вне рамок задачи).
// ============================================================================

import { markRead as markConversationRead } from '@/lib/chatBridge';
import { openConversation } from '@/lib/chatNavigation';
import { navigateTo } from '@/lib/navigate';
import { getSDK } from '@/store/sdkStore';
import {
  NOTIFICATIONS_READ_EVENT,
  type AppNotification,
} from '@/lib/notifications/notificationModel';

/** Запрос фокуса поля ввода живёт ограниченно: чат может не успеть открыться. */
const FOCUS_REQUEST_TTL_MS = 30_000;

/** Событие «фокус в поле ввода чата» (для уже открытого чата). */
export const CHAT_FOCUS_INPUT_EVENT = 'hydra:chat-focus-input';

interface FocusRequest {
  convId: string;
  ts: number;
}

let pendingFocus: FocusRequest | null = null;

/** Запросить фокус поля ввода чата (срабатывает при открытии/смене диалога). */
export function requestChatInputFocus(convId: string): void {
  if (!convId) return;
  pendingFocus = { convId, ts: Date.now() };
  window.dispatchEvent(new CustomEvent(CHAT_FOCUS_INPUT_EVENT, { detail: { convId } }));
}

/**
 * Снять запрос фокуса для диалога. true — фокус нужно поставить СЕЙЧАС
 * (запрос не просрочен и адресован этому чату).
 */
export function consumeChatInputFocus(convId: string): boolean {
  if (!pendingFocus || !convId) return false;
  if (pendingFocus.convId !== convId) return false;
  const fresh = Date.now() - pendingFocus.ts <= FOCUS_REQUEST_TTL_MS;
  pendingFocus = null;
  return fresh;
}

/** Можно ли ответить из карточки (только сообщения чата с известным диалогом). */
export function canReplyToNotification(n: AppNotification): boolean {
  return n.kind === 'chat' && !!n.convId;
}

/** «Открыть»: чат (dialogue) либо внутренний маршрут платформенного уведомления. */
export function openNotification(n: AppNotification): void {
  if (n.convId) {
    openConversation(n.convId);
    return;
  }
  if (n.url) navigateTo(n.url);
}

/** «Ответить»: открыть чат и сфокусировать поле ввода. */
export function replyToNotification(n: AppNotification): void {
  const convId = String(n.convId || '');
  if (!convId) return;
  requestChatInputFocus(convId);
  openConversation(convId);
}

/**
 * «Прочитать»: чат — markConversationRead; платформенное уведомление —
 * MarkNotificationsRead (существующий API отмечает все непрочитанные).
 */
export async function markNotificationRead(n: AppNotification): Promise<void> {
  if (n.kind === 'chat' && n.convId) {
    await markConversationRead(n.convId);
    return;
  }
  await getSDK().metadata.markNotificationsRead({});
  window.dispatchEvent(new CustomEvent(NOTIFICATIONS_READ_EVENT, { detail: { all: true, id: n.id } }));
}

/** Есть ли действие «Прочитать» для этой карточки. */
export function canMarkNotificationRead(n: AppNotification): boolean {
  return (n.kind === 'chat' && !!n.convId) || n.kind === 'platform';
}
